import { useMemo } from "react";
import CommentNode from "./CommentNode.js";
import {
  clubCommentMatchesSearch,
  commentRowEffectiveId,
  logCommentThreadRootSortDiagnostics,
  rowParentCommentIdForThread,
  sortCommentRootNodesInPlace as sortRootNodesInPlace,
} from "../utils/clubFeedPostUtils.js";

function normalizeId(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function hasParentMetadata(list) {
  return list.some((c) => rowParentCommentIdForThread(c) != null);
}

/**
 * Structural threading: true roots = no in-list parent. Missing-parent replies become
 * display roots (orphan tier); all root-tier nodes share one New/Old/Top pass after merge.
 */
function buildThreadTree(list) {
  const byId = new Map();
  const structuralRoots = [];
  const orphanRoots = [];

  list.forEach((c, idx) => {
    const id = commentRowEffectiveId(c);
    if (id == null) return;
    byId.set(id, { ...c, __order: idx, __children: [] });
  });

  list.forEach((c, idx) => {
    const id = commentRowEffectiveId(c);
    if (id == null) return;
    const node = byId.get(id);
    if (!node) return;
    node.__order = idx;

    const parentId = rowParentCommentIdForThread(c);
    if (parentId == null) {
      structuralRoots.push(node);
      return;
    }
    const parent = byId.get(parentId);
    if (parent) {
      parent.__children.push(node);
      return;
    }
    orphanRoots.push(node);
  });

  const sortByOrder = (arr) => {
    arr.sort((a, b) => a.__order - b.__order);
    arr.forEach((n) => {
      if (n.__children?.length) sortByOrder(n.__children);
    });
  };
  sortByOrder(structuralRoots);
  sortByOrder(orphanRoots);
  return { structuralRoots, orphanRoots };
}

/** Index: parent comment id -> child rows in list order (for honest threading from flat data). */
function buildChildrenIndexByParentId(list, byId) {
  const childrenByParent = new Map();
  list.forEach((c, listOrder) => {
    const id = commentRowEffectiveId(c);
    if (id == null) return;
    const pid = rowParentCommentIdForThread(c);
    if (pid == null || !byId.has(pid)) return;
    if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
    const arr = childrenByParent.get(pid);
    if (arr.some((x) => commentRowEffectiveId(x.row) === id)) return;
    arr.push({ row: c, order: listOrder });
  });
  for (const [, arr] of childrenByParent) {
    arr.sort((a, b) => a.order - b.order);
  }
  return childrenByParent;
}

function isStructuralRootRow(c, byId) {
  const id = commentRowEffectiveId(c);
  if (id == null) return false;
  const pid = rowParentCommentIdForThread(c);
  return pid == null || !byId.has(pid);
}

/**
 * Reddit-style search on the flat list: keep matching nodes, ancestors in loaded data, and
 * matching subtrees — without promoting replies to fake roots when the parent row exists
 * in `list` (fixes wrong trees from buildThreadTree orphan promotion).
 */
function filterFlatListToSearchTree(list, rawQuery) {
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return [];

  const byId = new Map();
  const orderById = new Map();
  list.forEach((c, i) => {
    const id = commentRowEffectiveId(c);
    if (id != null) {
      byId.set(id, c);
      orderById.set(id, i);
    }
  });

  const childrenByParent = buildChildrenIndexByParentId(list, byId);

  const recStack = new Set();
  function walk(cid) {
    const idKey = normalizeId(cid);
    if (idKey == null) return null;
    if (recStack.has(idKey)) return null;
    const node = byId.get(idKey);
    if (!node) return null;
    const selfId = commentRowEffectiveId(node) ?? idKey;
    recStack.add(idKey);
    try {
      const childEntries = childrenByParent.get(selfId) || [];
      const filteredChildren = childEntries
        .map(({ row: ch }) => walk(commentRowEffectiveId(ch)))
        .filter(Boolean);
      const hit = clubCommentMatchesSearch(node, q);
      if (!hit && filteredChildren.length === 0) return null;
      const order = orderById.get(idKey) ?? orderById.get(selfId) ?? 0;
      return { ...node, __order: order, __children: filteredChildren };
    } finally {
      recStack.delete(idKey);
    }
  }

  const rootRows = [];
  const seenRoot = new Set();
  list.forEach((c) => {
    const id = commentRowEffectiveId(c);
    if (id == null || seenRoot.has(id)) return;
    if (!isStructuralRootRow(c, byId)) return;
    seenRoot.add(id);
    const t = walk(id);
    if (t) rootRows.push(t);
  });

  return rootRows;
}

function findPathToTarget(nodes, targetId, trail = []) {
  const t = normalizeId(targetId);
  if (t == null) return null;
  for (const node of nodes || []) {
    const id = commentRowEffectiveId(node);
    if (id == null) continue;
    const nextTrail = [...trail, id];
    if (id === t) return nextTrail;
    const found = findPathToTarget(node.__children || [], t, nextTrail);
    if (found) return found;
  }
  return null;
}

export default function CommentThreadList({
  comments,
  viewerId,
  canParticipate,
  activeReplyTargetId,
  setActiveReplyTargetId,
  inlineReplyText,
  setInlineReplyText,
  inlineSending,
  onSubmitInlineReply,
  onContinueThread,
  highlightedCommentId,
  slug,
  postId,
  commentSurface = "club",
  artworkId = null,
  onToggleCommentLike,
  onShareComment,
  expandFirstLevelChildren = false,
  focusCommentId = null,
  canModerateComments = false,
  onCommentMutated,
  onCommentActionError,
  rootCommentSort = "new",
  searchActive = false,
  searchQuery = "",
}) {
  const list = Array.isArray(comments) ? comments : [];

  const hasMeta = useMemo(() => hasParentMetadata(list), [list]);

  const treeFull = useMemo(() => {
    if (list.length === 0) return [];
    if (!hasMeta) {
      const rootsFlat = [...list];
      sortRootNodesInPlace(rootsFlat, rootCommentSort);
      return rootsFlat;
    }
    const { structuralRoots, orphanRoots } = buildThreadTree(list);
    const combined = [...structuralRoots, ...orphanRoots];
    sortRootNodesInPlace(combined, rootCommentSort);
    if (typeof window !== "undefined" && window.__DEBUG_COMMENT_SORT__) {
      logCommentThreadRootSortDiagnostics(list, {
        rootCommentSort,
        commentSurface,
        artworkId,
        postId,
      });
    }
    return combined;
  }, [list, hasMeta, rootCommentSort, commentSurface, artworkId, postId]);

  const displayRoots = useMemo(() => {
    if (!searchActive || !String(searchQuery || "").trim()) {
      return treeFull;
    }
    if (!hasMeta) {
      return treeFull.filter((c) => clubCommentMatchesSearch(c, searchQuery));
    }
    const filtered = filterFlatListToSearchTree(list, searchQuery);
    sortRootNodesInPlace(filtered, rootCommentSort);
    return filtered;
  }, [list, treeFull, searchActive, searchQuery, hasMeta, rootCommentSort]);

  const focusPath = useMemo(() => {
    const rootsForPath =
      searchActive && String(searchQuery || "").trim() ? displayRoots : treeFull;
    return findPathToTarget(rootsForPath, focusCommentId) || [];
  }, [treeFull, displayRoots, searchActive, searchQuery, focusCommentId]);

  const forceExpandForIds = useMemo(() => new Set(focusPath.slice(0, -1)), [focusPath]);

  if (list.length === 0) return null;

  if (displayRoots.length === 0) {
    if (searchActive && String(searchQuery || "").trim()) {
      return (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-gray-400"
          role="status"
        >
          No comments match your search.
        </div>
      );
    }
    return null;
  }

  return (
    <>
      {displayRoots.map((comment, rootIdx) => (
        <CommentNode
          key={String(commentRowEffectiveId(comment) ?? `root-${rootIdx}`)}
          comment={comment}
          viewerId={viewerId}
          canParticipate={canParticipate}
          depth={0}
          children={comment.__children || []}
          activeReplyTargetId={activeReplyTargetId}
          setActiveReplyTargetId={setActiveReplyTargetId}
          inlineReplyText={inlineReplyText}
          setInlineReplyText={setInlineReplyText}
          inlineSending={inlineSending}
          onSubmitInlineReply={onSubmitInlineReply}
          onContinueThread={onContinueThread}
          highlightedCommentId={highlightedCommentId}
          slug={slug}
          postId={postId}
          commentSurface={commentSurface}
          artworkId={artworkId}
          onToggleCommentLike={onToggleCommentLike}
          onShareComment={onShareComment}
          forceExpandForIds={forceExpandForIds}
          forceExpandDirectChildren={expandFirstLevelChildren}
          canModerateComments={canModerateComments}
          onCommentMutated={onCommentMutated}
          onCommentActionError={onCommentActionError}
        />
      ))}
    </>
  );
}
